import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToggleSwitch } from "risa-oasis-ui_v2";
import DynamicSingleSideTab from "../components/dynamicSingleSideTab/dynamicSingleSideTab";
import { getAuthConfig } from "../config/AuthConfigContext";
import {
  getCurrentFacilityId,
  isCurrentUserAdmin,
  isCurrentUserSuperAdmin,
  userLoggedInFacilityType,
} from "../services/memberManagementService";
import { useAuthSelector } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/userGeneralInfoStore";
import ChevronDown from "../svg/chevron-down";
import type { SettingsPageId, SettingsPageProps } from "../types";
import MembersPage from "./MembersPage";
import ProfilePage from "./ProfilePage";
import SecurityPage from "./SecurityPage";
import SuperAdminPage from "./SuperAdminPage";
import SupportPage from "./SupportPage";

const ALL_PAGES: {
  id: SettingsPageId;
  label: string;
  requiresAdmin?: boolean;
  requiresSuperAdmin?: boolean;
}[] = [
  { id: "profile", label: "Profile" },
  { id: "security", label: "Security" },
  { id: "members", label: "Members", requiresAdmin: true },
  { id: "support", label: "Support" },
  { id: "superadmin", label: "Super Admin", requiresSuperAdmin: true },
];

export function SettingsPage({ enabledPages, roleOptions }: SettingsPageProps) {
  const [sideNavElements, setSideNavElements] = useState<
    { id: string; label: string }[]
  >([
    { id: "profile", label: "Profile" },
    { id: "security", label: "Security" },
  ]);

  const [viewLegacy, setViewLegacy] = useState(false);
  const [showNavigation, setShowNavigation] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");

  const location = useLocation();

  const { data: provider } = useAuthSelector(
    (state: AuthRootState) => state.providerDetails,
  );

  const navigate = useNavigate();
  const config = getAuthConfig();

  useEffect(() => {
    const buildNavElements = () => {
      const navElements: { id: string; label: string }[] = [];

      ALL_PAGES.forEach((page) => {
        if (enabledPages && !enabledPages.includes(page.id)) return;
        if (page.requiresSuperAdmin && !isCurrentUserSuperAdmin()) return;
        if (
          page.requiresAdmin &&
          !isCurrentUserAdmin() &&
          !isCurrentUserSuperAdmin()
        )
          return;
        navElements.push({ id: page.id, label: page.label });
      });

      setSideNavElements(navElements);
    };

    buildNavElements();
  }, [provider, enabledPages]);

  useEffect(() => {
    const storedViewLegacy = UserStore.getItem(
      UserGeneralInfoContextKeys.VIEW_LEGACY,
    );
    setViewLegacy(storedViewLegacy != null ? Boolean(storedViewLegacy) : false);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    if (currentPath) {
      setActiveTab(currentPath);
    }
  }, [location.pathname]);

  const handleViewLegacyChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    UserStore.setItem(UserGeneralInfoContextKeys.VIEW_LEGACY, data.value);
    navigate("/");
    window.location.reload();
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    navigate(tabId);
  };

  const currentFacilityId = getCurrentFacilityId() || "";
  const isExternalFacility =
    userLoggedInFacilityType(currentFacilityId) === "external";

  return (
    <div className="settings--layout">
      {/* Sidebar */}
      <div
        className={`flex h-full ${
          showNavigation ? "settings--navigation" : "settings--navigation__hide"
        }`}
      >
        <div
          className={`settings--navigation__container flex flex-col overflow-hidden ${
            showNavigation ? "animate-slide-in w-full" : "animate-slide-out w-0"
          }`}
        >
          {/* Title */}
          <div
            className={`settings--navigation__header w-full truncate pl-2 text-lg font-bold leading-normal text-black ${
              showNavigation ? "animate-fade-up" : "animate-fade-up-out"
            }`}
            style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}
          >
            Settings
          </div>

          {/* Links */}
          <div
            className={`settings--navigation__body flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden ${
              showNavigation ? "animate-fade-down" : "animate-fade-down-out"
            }`}
          >
            {sideNavElements.map((sideNavElement, index) => (
              <div
                key={sideNavElement.id}
                className={`${
                  showNavigation ? "animate-stagger-in" : "animate-stagger-out"
                }`}
                style={{ transitionDelay: `${10 + index * 50}ms` }}
              >
                <DynamicSingleSideTab
                  id={sideNavElement.id}
                  label={sideNavElement.label}
                  isActive={activeTab === sideNavElement.id}
                  onTabClick={handleTabClick}
                />
              </div>
            ))}
          </div>

          {/* Legacy Toggle */}
          {isExternalFacility && (
            <div className="mt-auto border-t border-primaryGray-15 pt-3">
              <div className="flex flex-row items-center justify-start gap-2 px-2">
                <div className="text-sm text-primaryGray-11">
                  View Legacy Version
                </div>
                <ToggleSwitch
                  key={viewLegacy ? "legacy-on" : "legacy-off"}
                  id="view-legacy"
                  defaultChecked={viewLegacy}
                  onChangeEmit={handleViewLegacyChange}
                />
              </div>
            </div>
          )}

          {/* Version Number */}
          <div
            className={`px-2 pb-3 ${isExternalFacility ? "pt-2" : "mt-auto pt-3"}`}
          >
            <div className="text-xs text-primaryGray-11/60">
              {config?.version ? `v${config.version}` : ""}
            </div>
          </div>
        </div>

        {/* Toggle Icon */}
        <div className="settings--navigation__icon relative border border-primaryGray-15">
          <div
            className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer"
            style={{
              filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
              left: "-14px",
              top: "22px",
            }}
            onClick={() => setShowNavigation(!showNavigation)}
          >
            <ChevronDown rotate={!showNavigation ? 270 : 90} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`settings--outlet px-3 pt-6 ${
          !showNavigation ? "settings--outlet__hide-left" : ""
        }`}
      >
        <div style={{ paddingTop: "1.5rem", height: "100%", minHeight: "0" }}>
          <Routes>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route
              path="members"
              element={<MembersPage roleOptions={roleOptions} />}
            />
            <Route path="support" element={<SupportPage />} />
            <Route path="superadmin" element={<SuperAdminPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
